import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Users, Mail, FolderKanban, CreditCard } from "lucide-react";
import { api, getUser, clearAuth } from "../lib/api";
import Logo from "../components/Logo";

export default function AdminDashboard() {
  const nav = useNavigate();
  const user = getUser();
  const [tab, setTab] = useState("leads");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/leads").then((r) => setLeads(r.data.leads || []));
    api.get("/admin/projects").then((r) => setProjects(r.data.projects || []));
  }, []);

  const logout = () => { clearAuth(); nav("/admin/login"); };

  const statCards = [
    { icon: Mail, label: "Total Leads", value: stats?.leads ?? "—", id: "leads" },
    { icon: Users, label: "Clients", value: stats?.clients ?? "—", id: "clients" },
    { icon: FolderKanban, label: "Projects", value: stats?.projects ?? "—", id: "projects" },
    { icon: CreditCard, label: "Paid Payments", value: stats?.paid_payments ?? "—", id: "payments" },
  ];

  return (
    <div className="bg-[#FDFBF7] min-h-screen">
      <header className="bg-white border-b border-[#EBE8DF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-[76px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-4" data-testid="admin-logo">
            <Logo height={42} />
            <span className="label-eyebrow border-l border-[#EBE8DF] pl-4">Admin</span>
          </Link>
          <div className="flex items-center gap-5">
            <span className="text-sm text-[#5C5C5C] hidden md:inline">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="btn-secondary py-2 px-4 text-sm"><LogOut size={14}/> Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <p className="label-eyebrow">Operations</p>
        <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">Dashboard</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
          {statCards.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.id} data-testid={`admin-stat-${s.id}`} className="border border-[#EBE8DF] bg-white p-6">
                <Icon size={18} className="text-[#C5A059]"/>
                <p className="font-display text-4xl mt-4">{s.value}</p>
                <p className="label-eyebrow mt-2">{s.label}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-12 flex gap-8 border-b border-[#EBE8DF]">
          {["leads", "projects"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
              className={`pb-3 -mb-px text-[14px] tracking-wide uppercase font-bold ${tab === t ? "border-b-2 border-[#C5A059] text-[#1A1A1A]" : "text-[#8B8C89]"}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === "leads" && (
          <div className="mt-8 border border-[#EBE8DF] bg-white" data-testid="admin-leads-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#8B8C89] uppercase tracking-[0.15em] text-[11px]">
                  <th className="px-6 py-4">Date</th><th className="px-6 py-4">Name</th>
                  <th className="px-6 py-4">Email</th><th className="px-6 py-4">Company</th>
                  <th className="px-6 py-4">Website</th>
                  <th className="px-6 py-4">Goal</th><th className="px-6 py-4">Budget</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 ? (
                  <tr><td className="px-6 py-8 text-[#8B8C89]" colSpan="7">No leads yet.</td></tr>
                ) : leads.map((l) => (
                  <tr key={l.id} className="border-t border-[#EBE8DF]">
                    <td className="px-6 py-4 text-[#5C5C5C]">{new Date(l.created_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4 font-medium">{l.name}</td>
                    <td className="px-6 py-4">{l.email}</td>
                    <td className="px-6 py-4 text-[#5C5C5C]">{l.company || "—"}</td>
                    <td className="px-6 py-4 text-[#5C5C5C] truncate max-w-[200px]">{l.website || "—"}</td>
                    <td className="px-6 py-4 text-[#5C5C5C]">{l.project_type || "—"}</td>
                    <td className="px-6 py-4 text-[#5C5C5C]">{l.budget || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "projects" && (
          <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid="admin-projects-grid">
            {projects.length === 0 ? <p className="text-[#8B8C89]">No projects yet.</p> :
              projects.map((p) => (
                <div key={p.id} className="border border-[#EBE8DF] bg-white p-6">
                  <p className="label-eyebrow">{p.status}</p>
                  <h3 className="font-display text-2xl mt-2">{p.title}</h3>
                  <p className="text-sm text-[#5C5C5C] mt-1">{p.client_email}</p>
                  <p className="mt-3 text-[15px] text-[#5C5C5C]">{p.description}</p>
                  <p className="mt-4 text-xs text-[#8B8C89]">{(p.milestones || []).filter(m=>m.status==="done").length} / {(p.milestones || []).length} milestones complete</p>
                </div>
              ))}
          </div>
        )}
      </main>
    </div>
  );
}
